import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaArrowLeft, FaExpand } from 'react-icons/fa';
 
interface PostMediaProps {
  darkMode: boolean;
  mediaUrl?: string;
  mediaType?: 'image' | 'video';
}
 
export const PostMedia: React.FC<PostMediaProps> = ({ darkMode, mediaUrl, mediaType }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
 
  // Pinch-to-zoom — UNTOUCHED
  useEffect(() => {
    if (!isZoomed || !containerRef.current) return;
    let initialDistance = 0;
    let initialScale = 1;
 
    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        initialDistance = Math.hypot(
          e.touches[1].clientX - e.touches[0].clientX,
          e.touches[1].clientY - e.touches[0].clientY
        );
        initialScale = scale;
      }
    };
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const distance = Math.hypot(
          e.touches[1].clientX - e.touches[0].clientX,
          e.touches[1].clientY - e.touches[0].clientY
        );
        const newScale = Math.min(Math.max(1, initialScale * (distance / initialDistance)), 5);
        setScale(newScale);
        if (newScale === 1) setPosition({ x: 0, y: 0 });
      }
    };
 
    const el = containerRef.current;
    el.addEventListener('touchstart', handleTouchStart, { passive: false });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isZoomed, scale]);
 
  // Lock body scroll — UNTOUCHED
  useEffect(() => {
    document.body.style.overflow = isZoomed ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isZoomed]);
 
  // All hooks above — early return here
  if (!mediaUrl) return null;
 
  const handleOpenZoom = () => {
    setIsZoomed(true);
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };
 
  const handleCloseZoom = () => {
    setIsZoomed(false);
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };
 
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const newScale = Math.min(Math.max(1, scale + e.deltaY * -0.01), 5);
    setScale(newScale);
    if (newScale === 1) setPosition({ x: 0, y: 0 });
  };
 
  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };
 
  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };
 
  const handleMouseUp = () => setIsDragging(false);
 
  const handleImageClick = () => {
    if (scale === 1) {
      setScale(2);
    } else {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  };
 
  // ── Fullscreen portal — UNTOUCHED logic, rethemed visuals
  const fullscreenModal = isZoomed
    ? ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            width: '100vw',
            height: '100dvh',
            zIndex: 99999,
            backgroundColor: '#0d1a1a',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Back button — teal/ink */}
          <div
            style={{
              padding: '12px 16px',
              flexShrink: 0,
              borderBottom: '1px solid rgba(90,184,184,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <button
              onClick={handleCloseZoom}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#7ecece',
                background: 'rgba(90,184,184,0.1)',
                border: '1.5px solid rgba(90,184,184,0.25)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 900,
                padding: '8px 14px',
                letterSpacing: '1px',
              }}
            >
              <FaArrowLeft size={12} />
              BACK
            </button>
 
            {/* Zoom indicator in header on mobile */}
            {scale > 1 && (
              <span
                style={{
                  color: '#e8a820',
                  fontSize: '11px',
                  fontWeight: 900,
                  letterSpacing: '1px',
                  background: 'rgba(232,168,32,0.1)',
                  border: '1.5px solid rgba(232,168,32,0.25)',
                  borderRadius: '6px',
                  padding: '4px 10px',
                }}
              >
                {Math.round(scale * 100)}%
              </span>
            )}
          </div>
 
          {/* Zoomable image — UNTOUCHED */}
          <div
            ref={containerRef}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
            }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <img
              src={mediaUrl}
              alt="Post media fullscreen"
              draggable={false}
              onClick={handleImageClick}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                userSelect: 'none',
                transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                transformOrigin: 'center',
                transition: isDragging ? 'none' : 'transform 0.15s ease',
              }}
            />
          </div>
 
          {/* Tap hint — only on mobile, only at scale=1 */}
          {scale === 1 && (
            <div
              style={{
                position: 'absolute',
                bottom: 24,
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(13,26,26,0.85)',
                border: '1px solid rgba(90,184,184,0.2)',
                color: 'rgba(126,206,206,0.6)',
                fontSize: '10px',
                fontWeight: 900,
                letterSpacing: '1px',
                padding: '4px 14px',
                borderRadius: '999px',
                pointerEvents: 'none',
                userSelect: 'none',
                backdropFilter: 'blur(8px)',
              }}
            >
              TAP TO ZOOM
            </div>
          )}
        </div>,
        document.body
      )
    : null;
 
  return (
    <>
      {/* ── Inline media — fluid width, wraps the card ── */}
      <div className="onyx-post-media">
        {mediaType === 'video' ? (
          <div
            className="onyx-media-video"
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
              border: `1.5px solid ${darkMode ? 'rgba(90,184,184,0.12)' : 'rgba(13,26,26,0.1)'}`,
              background: '#000',
            }}
          >
            <video
              src={mediaUrl}
              controls
              style={{
                width: '100%',
                display: 'block',
                objectFit: 'contain',
                maxHeight: '420px',
              }}
            />
          </div>
        ) : (
          <div
            className="onyx-media-image"
            onClick={handleOpenZoom}
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
              border: `1.5px solid ${darkMode ? 'rgba(90,184,184,0.12)' : 'rgba(13,26,26,0.1)'}`,
              cursor: 'zoom-in',
              position: 'relative',
            }}
          >
            <img
              src={mediaUrl}
              alt="Post media"
              loading="lazy"
              style={{
                width: '100%',
                display: 'block',
                objectFit: 'cover',
                maxHeight: '400px',
              }}
            />
            {/* Expand hint overlay on hover */}
            <div
              className="onyx-media-expand-hint"
              style={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                background: 'rgba(13,26,26,0.7)',
                border: '1px solid rgba(90,184,184,0.3)',
                color: '#7ecece',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                opacity: 0,
                transition: 'opacity 0.2s',
                backdropFilter: 'blur(4px)',
                pointerEvents: 'none',
              }}
            >
              <FaExpand size={9} />
              <span style={{ fontWeight: 900, letterSpacing: '0.5px' }}>EXPAND</span>
            </div>
          </div>
        )}
      </div>
 
      {fullscreenModal}
    </>
  );
};